import { Injectable, NgZone, QueryList, inject } from '@angular/core';
import { CtGenLookupComponent } from '../components/ct-gen-lookup/ct-gen-lookup.component';
import { DxFormComponent } from 'devextreme-angular';
import { DlistFieldDto } from '../models/ortak.dtos';
import { timer } from 'rxjs';
import { CtGenRadioBtnComponent } from '../components/ct-gen-radio-btn/ct-gen-radio-btn.component';

/** DKM-5148 Clara: kayıt anında eksik/hatalı alan (etiket + zorunluluk dışı kural mesajı). */
export interface EksikAlan {
  etiket: string;
  mesaj?: string;
}

/** DKM-5148 Clara: hiçbir alan tespit edilemezse kullanılan genel uyarı (eski metin). */
export const EKSIK_ALAN_VARSAYILAN_MESAJ = 'Lütfen form alanlarını uygun şekilde doldurunuz!';

@Injectable({
  providedIn: 'root',
})
export class ValidationService {
  validationRulesMap: { [key: string]: any[] } = {};

  /**
   * DKM-5148 Clara: son isFormValid turunda (aynı senkron blokta yapılan çağrılar) bulunan eksik/hatalı alanlar.
   * Ekran sırasına (DOM) göre dizilir; aynı etiket bir kez yazılır.
   */
  sonEksikAlanlar: EksikAlan[] = [];
  private eksikAlanElemanlari: (Element | null)[] = [];
  private eksikToplaniyor = false;
  private eksikTurAcik = false;
  private eksikTurFormu: DxFormComponent | null = null;
  // DKM-5168: tur sonu sıfırlaması zone dışında (bkz. eksikTurBaslat)
  // (spec'lerde new ValidationService() ile DI dışında da kurulur → zone yoksa düz mikro görev)
  private readonly zone: NgZone | null = (() => { try { return inject(NgZone); } catch { return null; } })();

  constructor() {}

  initializeValidationRules(metadata: any[] = []): { [key: string]: any[] } {
    const validationRulesMap: { [key: string]: any[] } = {};

    if (!Array.isArray(metadata)) {
      console.error('Metadata should be an array');
      return validationRulesMap;
    }

    metadata.forEach((field: any) => {
      validationRulesMap[field.dataField] = field.validators?.map(
        (validator: any) => {
          if (validator.type === 'pattern') {
            return {
              type: 'pattern',
              pattern: field.pattern,
              message: 'Geçersiz Değer',
            };
          }
          const rule: any = {
            type: validator.type,
            message: this.getValidationMessage(
              validator.type,
              validator.min,
              validator.max
            ),
            min: validator.min,
            max: validator.max,
          };
          if (rule?.min === 0 && rule?.type !== 'stringLength') rule.min = 1;
          if (rule?.max === 0) rule.max = 2;
          return rule;
        }
      );
    });
    return validationRulesMap;
  }

  getValidationMessage(type: string, min: number, max: number): string {
    switch (type) {
      case 'required':
        return 'Bu alan zorunludur';
      case 'numeric':
        return 'Bu alan sayısal olmalıdır';
      case 'stringLength':
        return `min: ${min} max: ${max} karakter olabilir.`;
      default:
        return 'Geçersiz değer';
    }
  }

  getRequiredValidationsForEdit(
    config: any,
    keys: string[],
    formData: any,
    componentInstance: any
  ): boolean {
    let formValid = true;

    keys.forEach((key: string) => {
      const hasRequiredRule = config[key]?.some(
        (rule: any) => rule.type === 'required'
      );
      if (hasRequiredRule) {
        const value = formData[key];
        if (value !== null && value !== undefined) {
          if (componentInstance.hasOwnProperty(`${key}Valid`)) {
            componentInstance[`${key}Valid`] = true;
          }
        } else {
          if (componentInstance.hasOwnProperty(`${key}Valid`)) {
            componentInstance[`${key}Valid`] = false;
            formValid = false;
          }
        }
      }
    });

    return formValid;
  }

  validateAllLookups(lookups: QueryList<CtGenLookupComponent>): boolean {
    if (lookups == null) {
      return true;
    } else {
      let allValid = true;

      lookups.forEach((lookup) => {
        // DKM-5148 Clara: eski hâli
        // if (!lookup.validator?.instance?.validate().isValid) {
        const sonuc: any = lookup.validator?.instance?.validate();
        if (!sonuc?.isValid) {
          allValid = false;
          // DKM-5148 Clara: yalnız isFormValid turunda eksik alan listesine yazar
          this.eksikAlanKaydet(
            lookup?.label || this.domEtiketi(lookup?.validator?.instance?.element?.()) || lookup?.dataField,
            sonuc?.brokenRules?.[0],
            lookup?.validator?.instance?.element?.()
          );
        }
      });
      return allValid;
    }
  }

  validateAllRadioButtons(radioButtons: QueryList<CtGenRadioBtnComponent>): boolean {
    if (radioButtons == null) {
      return true;
    } else {
      let allValid = true;

      radioButtons.forEach((radioButton) => {
        if (!radioButton?.isRadioBtnValid) {
          allValid = false;
          // DKM-5148 Clara: radyonun kendi etiketi yok → formdaki dxi-item etiketi, yoksa dataField
          const radyoFormu = radioButton?.form ?? this.eksikTurFormu;
          const etiket = this.formItemEtiketi(radyoFormu, radioButton?.dataField);
          this.eksikAlanKaydet(etiket || radioButton?.dataField, undefined, this.etiketliAlanElemani(radyoFormu, etiket));
        }
      });
      return allValid;
    }  
  }

  validateAllControls(controls: QueryList<any>): boolean {
    if (controls == null) {
      return true;
    } else {
      let allValid = true;
      controls.forEach((control) => {
        // DKM-5148 Clara: eski hâli
        // if (
        //   !control.ctlSablonComponent?.validator?.instance?.validate().isValid
        // ) {
        const sonuc: any = control.ctlSablonComponent?.validator?.instance?.validate();
        if (!sonuc?.isValid) {
          allValid = false;
          // DKM-5148 Clara: dk-ctl-* → labelText, yoksa dış dxi-item etiketi
          const el = control.ctlSablonComponent?.validator?.instance?.element?.();
          this.eksikAlanKaydet(
            control?.labelText || control?.ctlSablonComponent?.labelText || this.domEtiketi(el) ||
              control?.dataField || control?.ctlSablonComponent?.dataField,
            sonuc?.brokenRules?.[0],
            el
          );
        }
      });
      return allValid;
    }
  }

  validateFormWihoutCtlLkp(form: DxFormComponent): boolean {
    // DKM-5148 Clara: eski hâli
    // return form?.instance?.validate().isValid;
    const sonuc: any = form?.instance?.validate();
    // DKM-5148 Clara: kırılan kuralların alan etiketleri (yalnız isFormValid turunda yazılır)
    (sonuc?.brokenRules ?? []).forEach((kural: any) => {
      const v = kural?.validator;
      let item: any = null;
      try {
        const getter = v?.option?.('dataGetter');
        item = typeof getter === 'function' ? getter()?.formItem : null;
      } catch {
        item = null;
      }
      const el = v?.element?.();
      this.eksikAlanKaydet(
        item?.label?.text || this.domEtiketi(el) || item?.dataField || v?.option?.('name'),
        kural,
        el
      );
    });
    return sonuc?.isValid;
  }

  isFormValid(
    form: DxFormComponent,
    lookups: QueryList<CtGenLookupComponent>,
    controls: QueryList<any>,
    radioBtns?: QueryList<CtGenRadioBtnComponent>
  ): boolean {
    // DKM-5148 Clara: aynı turdaki (senkron blok) isFormValid çağrılarının eksik alanlarını topla.
    // Doğrulama/dönüş mantığı değişmedi; yalnız liste yan çıktı olarak saklanır (eksikAlanMesaji()).
    this.eksikTurBaslat();
    this.eksikToplaniyor = true;
    this.eksikTurFormu = form;
    try {
      return this.isFormValidIc(form, lookups, controls, radioBtns);
    } finally {
      this.eksikToplaniyor = false;
      this.eksikTurFormu = null;
    }
  }

  // DKM-5148 Clara: isFormValid'in eski gövdesi aynen buraya taşındı (davranış değişmedi)
  private isFormValidIc(
    form: DxFormComponent,
    lookups: QueryList<CtGenLookupComponent>,
    controls: QueryList<any>,
    radioBtns?: QueryList<CtGenRadioBtnComponent>
  ): boolean {
    const _formValid =
      form != null ? this.validateFormWihoutCtlLkp(form) : true;
    const _lookupsValid =
      lookups != null ? this.validateAllLookups(lookups) : true;
    const _controlsValid =
      controls != null ? this.validateAllControls(controls) : true;
    const _radioBtnsValid =
      radioBtns != null ? this.validateAllRadioButtons(radioBtns) : true;
    return _formValid && _lookupsValid && _controlsValid && _radioBtnsValid;
  }

  // Required olan alanları null yapacak metod
  setRequiredFieldsToNullIfEmpty(validationRulesMap: any, formData: any): any {
    const updatedFormData = { ...formData }; // Form verisini kopyala

    Object.keys(validationRulesMap).forEach((key) => {
      const fieldValidationRules = validationRulesMap[key];

      // Required validation'ı olan alanları kontrol et
      const isRequired = fieldValidationRules.some(
        (rule: any) => rule.type === 'required'
      );

      // Eğer required ise ve alanın değeri 0, undefined veya boşsa null yap
      //  ve formData'da ilgili alan varsa ( extend edildiğinde validationrulesMap'de formData'da olmayan alanlar olabilir)
      if (
        isRequired &&
        key in updatedFormData &&
        (updatedFormData[key] === 0 ||
          updatedFormData[key] === undefined ||
          updatedFormData[key] === '')
      ) {
        updatedFormData[key] = null;
      }
    });

    return updatedFormData; // Güncellenmiş form verisini geri döner
  }

  findDifferences(obj1: any, obj2: any): string {
    let differencesHtml = '<ul>';

    for (const key in obj1) {
      if (obj1.hasOwnProperty(key) && obj2.hasOwnProperty(key)) {
        let value1 = obj1[key];
        let value2 = obj2[key];

        // Eğer her iki değer de array ise, boş array'leri eşitle
        if (Array.isArray(value1) && Array.isArray(value2)) {
          value1 = value1.length === 0 ? null : value1;
          value2 = value2.length === 0 ? null : value2;
        }

        // Eğer birisi undefined, null ya da boş string (""), boş array ise eşitle
        if (
          value1 == null ||
          value1 === '' ||
          (Array.isArray(value1) && value1.length === 0)
        ) {
          value1 = null;
        }
        if (
          value2 == null ||
          value2 === '' ||
          (Array.isArray(value2) && value2.length === 0)
        ) {
          value2 = null;
        }

        // İlk ve son değer karşılaştırması: Farklılık varsa ve anlamlıysa
        if (value1 !== value2) {
          differencesHtml += `
          <li><b>${key}:</b> Eski Değer - <b style="color: red">${
            value2 || 'Boş'
          }</b>, Yeni Değer - <b style="color: green">${
            value1 || 'Boş'
          }</b></li>
        `;
        }
      }
    }

    differencesHtml += '</ul>';

    return differencesHtml; // HTML formatında farklılıkları döndürür
  }


  setFormItemEditorOptions(
    fields: DlistFieldDto[],
    form?: DxFormComponent,
    lookups?: QueryList<CtGenLookupComponent>,
    controls?: QueryList<any>,
    setValidationRules: boolean = false
  ): void {
    try {
      if (form && fields?.length > 0) {
        const fieldsMap: Map<string, DlistFieldDto> = new Map<string,DlistFieldDto>();

        fields.forEach((field: DlistFieldDto) => {
          fieldsMap.set(field.dataField, field);
        });
        
        if(lookups?.length > 0){

          lookups?.forEach((lookup:CtGenLookupComponent) => {
              lookup.setWidth(fieldsMap.get(lookup.dataField)?.editorOptions?.width);
        });

      }
      
        this.recursiveSetFormItemEditorOptions(
          form.instance.option('items'),fieldsMap
        );


      } else {
        console.error('Form veya alanlar geçersiz');
        return;
      }
    } catch (error) {
      console.error('Generic editor options Hata:', error);
    }
  }

  private recursiveSetFormItemEditorOptions(
    items: any,
    fieldsMap: Map<string, DlistFieldDto>
  ) {
    items.forEach((item: any) => {
      if (item?.itemType == 'group') {
        this.recursiveSetFormItemEditorOptions(item.items, fieldsMap);
      } else if (item?.dataField != null) {
        const field = fieldsMap.get(item.dataField);

        if (
          field != null &&
          item?.template == null &&
          item?.editorType != 'dxDateBox'
        ) {
          item.editorOptions = {
            ...item.editorOptions,
            ...field.editorOptions,
          };
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // DKM-5148 Clara: kayıt anında eksik alan adları listesi
  // Kullanım: if (this.validationService.isFormValid(...)) {...} else {
  //   alert(this.validationService.eksikAlanMesaji('<ekranın eski genel mesajı>'), 'Uyarı!'); }
  // ---------------------------------------------------------------------------

  /**
   * Bu turda isFormValid'in bulduğu eksik/hatalı alanlar (ekran sırasıyla).
   * isFormValid bu turda çağrılmadıysa boş döner (eski bir doğrulamanın listesi gösterilmesin).
   */
  eksikAlanlar(): EksikAlan[] {
    return this.eksikTurAcik ? [...this.sonEksikAlanlar] : [];
  }

  /**
   * Uyarı penceresi (devextreme alert) için HTML mesaj:
   * "Aşağıdaki alanlar eksik veya hatalı:" + madde listesi.
   * Hiçbir alan tespit edilemezse `varsayilan` (ekranın eski genel mesajı) döner.
   */
  eksikAlanMesaji(varsayilan: string = EKSIK_ALAN_VARSAYILAN_MESAJ): string {
    const liste = this.eksikAlanlar();
    if (!liste.length) return varsayilan;
    const satirlar = liste
      .map((x) => `<li><b>${this.htmlKacis(x.etiket)}</b>${x.mesaj ? ' — ' + this.htmlKacis(x.mesaj) : ''}</li>`)
      .join('');
    return `Aşağıdaki alanlar eksik veya hatalı:<ul style="margin:6px 0 0 18px;padding:0">${satirlar}</ul>`;
  }

  /** Yeni bir senkron tur başladıysa listeyi sıfırlar; tur, mikro görev kuyruğunda kapanır. */
  private eksikTurBaslat(): void {
    if (this.eksikTurAcik) return;
    this.eksikTurAcik = true;
    this.sonEksikAlanlar = [];
    this.eksikAlanElemanlari = [];
    // DKM-5168 eski hâli: mikro görev Angular zone'unda açılıyordu; bitince değişiklik algılaması yeniden
    // çalışıyor, isFormValid()'i şablonda ([disabled] vb.) çağıran formlarda bu yine buraya gelip
    // sonsuz döngü kuruyordu (Beyansız/Ceza Tahakkuk "Ekle" ekran kilitlenmesi).
    // Promise.resolve().then(() => (this.eksikTurAcik = false));
    const sifirla = () => Promise.resolve().then(() => (this.eksikTurAcik = false));
    if (this.zone) this.zone.runOutsideAngular(sifirla); else sifirla();
  }

  private eksikAlanKaydet(etiket: any, kural?: any, eleman?: any): void {
    if (!this.eksikToplaniyor) return;
    let e = (etiket ?? '').toString().replace(/\s+/g, ' ').trim();
    e = e.replace(/\s*[:*]+\s*$/, '').trim();
    if (!e || this.sonEksikAlanlar.some((x) => x.etiket === e)) return;
    // Zorunluluk kuralının mesajı ("Bu alan zorunludur") tekrar yazılmaz; uzunluk/desen vb. kuralların mesajı eklenir.
    const mesaj = kural && kural.type !== 'required' && kural.message ? String(kural.message) : undefined;
    const el: Element | null = eleman instanceof Element ? eleman : eleman?.[0] instanceof Element ? eleman[0] : null;
    // DOM sırasına göre araya yerleştir (elemanı olmayanlar sona)
    let i = this.sonEksikAlanlar.length;
    if (el) {
      for (let k = 0; k < this.eksikAlanElemanlari.length; k++) {
        const diger = this.eksikAlanElemanlari[k];
        if (!diger || diger.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) {
          i = k;
          break;
        }
      }
    }
    this.sonEksikAlanlar.splice(i, 0, { etiket: e, mesaj });
    this.eksikAlanElemanlari.splice(i, 0, el);
  }

  /** Elemanın içinde bulunduğu form alanının (dx-field-item) görünen etiketi; iç içe formlarda dolu olan ilk etiket. */
  private domEtiketi(eleman: any): string {
    try {
      let el: Element | null = eleman instanceof Element ? eleman : eleman?.[0] instanceof Element ? eleman[0] : null;
      while (el) {
        const alan: Element | null = el.closest('.dx-field-item');
        if (!alan) return '';
        const lbl = alan.querySelector(':scope > .dx-field-item-label .dx-field-item-label-text, :scope > label .dx-field-item-label-text');
        let metin = (lbl?.textContent ?? '').trim();
        // labelMode floating/static: etiket editörün içinde çizilir
        if (!metin) metin = (alan.querySelector('.dx-texteditor-label .dx-label')?.textContent ?? '').trim();
        if (metin) return metin;
        el = alan.parentElement;
      }
    } catch {
      /* etiket bulunamazsa sessizce geç */
    }
    return '';
  }

  /** Formun item ağacında (grup/sekme dahil) dataField'ı verilen öğenin etiket metni. */
  private formItemEtiketi(form: DxFormComponent | null | undefined, dataField: string): string {
    if (!form?.instance || !dataField) return '';
    const ara = (items: any[]): string => {
      for (const item of items ?? []) {
        if (!item) continue;
        if (item.dataField === dataField || item.name === dataField) return item.label?.text || '';
        const alt = ara(item.items) || (item.tabs ?? []).map((t: any) => ara(t?.items)).find((x: string) => !!x) || '';
        if (alt) return alt;
      }
      return '';
    };
    try {
      return ara(form.instance.option('items') as any[]);
    } catch {
      return '';
    }
  }

  /** Formda görünen etiketi `etiket` olan alanın (dx-field-item) elemanı — ekran sırası için. */
  private etiketliAlanElemani(form: DxFormComponent | null | undefined, etiket: string): Element | null {
    if (!etiket) return null;
    try {
      const kok: any = form?.instance?.element?.();
      if (!(kok instanceof Element)) return null;
      const lbl = Array.from(kok.querySelectorAll('.dx-field-item-label-text') as NodeListOf<Element>).find(
        (x) => (x.textContent ?? '').replace(/\s*[:*]+\s*$/, '').trim() === etiket.trim()
      );
      return lbl?.closest('.dx-field-item') ?? null;
    } catch {
      return null;
    }
  }

  private htmlKacis(s: string): string {
    return String(s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
  }
}
